import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, currentYM } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageLoader } from "@/components/PageLoader";
import { TrendingUp, ChevronRight, Award, AlertTriangle } from "lucide-react";

const CAT_STYLE = {
  green: { chip: "bg-green-100 text-green-800 border-green-300", ring: "text-green-600", label: "TELADAN" },
  blue: { chip: "bg-blue-100 text-blue-800 border-blue-300", ring: "text-blue-600", label: "BAIK" },
  amber: { chip: "bg-amber-100 text-amber-800 border-amber-300", ring: "text-amber-600", label: "CUKUP" },
  red: { chip: "bg-red-100 text-red-800 border-red-300", ring: "text-red-600", label: "PERLU PERBAIKAN" },
};

export default function AdminPerformance() {
  const nav = useNavigate();
  const [period, setPeriod] = useState(currentYM());
  const [data, setData] = useState(null);
  const [locks, setLocks] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get("/admin/performance/summary", { params: { period } }),
      api.get("/admin/kasbon-locks").catch(() => ({ data: [] })),
    ]).then(([summ, l]) => {
      if (cancel) return;
      setData(summ.data);
      const map = {};
      (l.data || []).forEach((r) => { map[r.user_id] = r; });
      setLocks(map);
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  if (loading && !data) return <PageLoader label="Menghitung performa pegawai..." />;
  if (!data) return null;

  const list = data.employees || [];
  const avg = list.length > 0
    ? Math.round(list.reduce((s, e) => s + e.score, 0) / list.length * 10) / 10
    : 0;
  const teladan = list.filter((e) => e.color === "green").length;
  const perlu = list.filter((e) => e.color === "red").length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="admin-performance-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="overline">HRD</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Performa Pegawai</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Skor dihitung dari Kedisiplinan (50%) + Kehadiran (40%) + Bonus (+10 jika tanpa telat)
          </p>
        </div>
        <Input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-40"
          data-testid="perf-period-input"
        />
      </div>

      {/* KPI header */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-2 text-zinc-500">
            <TrendingUp className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Rata-rata Skor</p>
          </div>
          <p className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="perf-avg-score">
            {avg}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-2 text-green-700">
            <Award className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Teladan (≥90)</p>
          </div>
          <p className="font-display text-2xl font-bold tracking-tight text-green-700 mt-1" data-testid="perf-teladan-count">
            {teladan}
          </p>
        </Card>
        <Card className="p-4 border border-amber-300 bg-amber-50 shadow-none">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            <p className="overline text-[0.6rem]">Perlu Perbaikan (&lt;60)</p>
          </div>
          <p className="font-display text-2xl font-bold tracking-tight text-amber-800 mt-1" data-testid="perf-perlu-count">
            {perlu}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] text-zinc-500">Total Pegawai</p>
          <p className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="perf-total-count">
            {list.length}
          </p>
        </Card>
      </div>

      {/* Ranking list */}
      <div className="mt-6 space-y-2">
        {list.length === 0 && (
          <Card className="p-12 text-center border border-zinc-200 shadow-none">
            <p className="text-sm text-zinc-500">Belum ada data pegawai.</p>
          </Card>
        )}
        {list.map((e, idx) => {
          const style = CAT_STYLE[e.color] || CAT_STYLE.blue;
          return (
            <Card
              key={e.user_id}
              className="p-4 border border-zinc-200 shadow-none hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => nav(`/admin/performa/${e.user_id}?period=${period}`)}
              data-testid={`perf-row-${e.employee_id}`}
            >
              <div className="flex items-center gap-4">
                {/* Rank */}
                <div className="w-8 text-center flex-shrink-0">
                  <p className="font-display text-lg font-bold text-zinc-400">#{idx + 1}</p>
                </div>

                {/* Score gauge (mini) */}
                <div className="relative w-14 h-14 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e4e4e7" strokeWidth="10" />
                    <circle
                      cx="50" cy="50" r="42" fill="none"
                      className={style.ring}
                      stroke="currentColor" strokeWidth="10"
                      strokeDasharray={`${(e.score / 100) * 263.9} 263.9`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="font-display text-sm font-bold tracking-tight">{Math.round(e.score)}</p>
                  </div>
                </div>

                {/* Name */}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{e.name}</p>
                  <p className="text-xs text-zinc-500 font-mono">
                    {e.employee_id} · {e.pay_type === "hourly" ? "PHL" : "Bulanan"}
                  </p>
                  <Badge className={`${style.chip} mt-1 text-[0.6rem] font-bold border`} data-testid={`perf-badge-${e.employee_id}`}>
                    {style.label}
                  </Badge>
                  {locks[e.user_id]?.locked && (
                    <Badge className="ml-1 bg-red-100 text-red-800 border-red-300 border text-[0.6rem] font-bold" data-testid={`kasbon-lock-${e.employee_id}`} title={locks[e.user_id].reason}>
                      🔒 Kasbon Terkunci
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="hidden sm:grid grid-cols-3 gap-4 text-right text-xs flex-shrink-0">
                  <div>
                    <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Disiplin</p>
                    <p className="font-mono font-semibold">{e.punctuality_pct}%</p>
                  </div>
                  <div>
                    <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Hadir</p>
                    <p className="font-mono font-semibold">{e.attendance_pct}%</p>
                  </div>
                  <div>
                    <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Telat</p>
                    <p className="font-mono font-semibold">{e.days_late} hari</p>
                  </div>
                </div>

                <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
              </div>

              {/* Mobile stats row */}
              <div className="sm:hidden grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-zinc-100 text-center text-xs">
                <div>
                  <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Disiplin</p>
                  <p className="font-mono font-semibold">{e.punctuality_pct}%</p>
                </div>
                <div>
                  <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Hadir</p>
                  <p className="font-mono font-semibold">{e.attendance_pct}%</p>
                </div>
                <div>
                  <p className="text-[0.6rem] uppercase text-zinc-500 tracking-widest">Telat</p>
                  <p className="font-mono font-semibold">{e.days_late} hari</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
